import { FC, useMemo } from 'react'
import { replace, type RouteObject, useSubmit } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import Persona from 'persona-react'
import '../kyc.css'

// COMPONENTS
import { Column } from '@components/templates/column'

// TYPES
import type { Fields, FieldValue, InquiryOptions } from 'persona/dist/lib/interfaces'

// UTILITIES
import { PersonaAPI } from '@apis'
import { parseKycStatus, parseTeraUserData } from '@schemas'
import { createAction } from '@utils/actions'
import { createKycVerifiedLoader, useLoader } from '@utils/loaders'
import { getRequiredKycPhonePath } from '@utils/user-onboarding'


type KycStatus = 'approved' | 'completed' | 'created' | 'pending' | 'declined' | 'needs_review' | 'expired' | 'failed' | undefined

const KycVerifyScreen: FC = () => {
    const { isLoaded, isSignedIn, user } = useUser()
    const { id: inquiryId, sessionToken } = useLoader<typeof loader>()
    const submit = useSubmit()

    const fields = useMemo<Fields>(() => {
        const obj: Record<string, FieldValue | null | undefined> = {
            nameFirst: user?.firstName,
            nameLast: user?.lastName,
            birthdate: undefined,
            addressStreet1: undefined,
            addressCity: undefined,
            addressSubdivision: undefined,
            addressPostalCode: undefined,
            addressCountryCode: undefined,
            phoneNumber: user?.primaryPhoneNumber?.phoneNumber,
            emailAddress: user?.primaryEmailAddress?.emailAddress,
            customAttribute: undefined,
        }

        return Object.keys(obj).reduce<Fields>((prev, key) => (obj[key] != null ? { ...prev, [key]: obj[key] } : prev), {})
    }, [user?.firstName, user?.lastName, user?.primaryPhoneNumber?.phoneNumber, user?.primaryEmailAddress?.emailAddress])

    const handleCompleteEvent: InquiryOptions['onComplete'] = (args) => {
        const formData = new FormData()
        formData.set('inquiryId', inquiryId)
        formData.set('status', args.status)
        submit(formData, { method: 'POST' })
    }

    function logEvent(event: string, args?: Record<string, unknown>) {
        console.log(`Persona ${event} Event:`, args)
    }

    return (
        <Column.Content>
            <div className="persona-verification">
                {(isLoaded && isSignedIn && !!user?.id && !!inquiryId && !!sessionToken) && (
                    <Persona
                        referenceId={user.id}
                        inquiryId={inquiryId}
                        sessionToken={sessionToken}
                        environmentId={import.meta.env.VITE_PERSONA_ENVIRONMENT_ID}
                        fields={fields}
                        onCancel={(args) => logEvent('Cancel', args)}
                        onComplete={handleCompleteEvent}
                        onError={(args) => logEvent('Error', args)}
                        onLoad={() => logEvent('Load')}
                        onReady={() => logEvent('Ready')}
                    />
                )}
            </div>
        </Column.Content>
    )
}

const loader = createKycVerifiedLoader(async ({ request }, isKycVerified, isNewUser) => {
    const search = new URL(request.url).search
    const tera = parseTeraUserData(window.Clerk?.user?.publicMetadata.tera)
    if (!tera.address) return replace(`/sign-up/billing${search}`)

    const phonePath = getRequiredKycPhonePath(window.Clerk?.user, search)
    if (phonePath) return replace(phonePath)

    if (isKycVerified || !isNewUser) return replace('/account')

    const inquiry = await PersonaAPI.CreateInquiryRequest.fetch(window.Clerk?.user?.id)
    if (inquiry.status === 'approved' || inquiry.status === 'completed') return completeKyc(inquiry.id, inquiry.status, request)

    return inquiry
})

const action = createAction(async ({ request }) => {
    const formData = await request.formData()
    const inquiryId = `${formData.get('inquiryId')}`
    const status = `${formData.get('status')}`
    return completeKyc(inquiryId, status, request)
})

function completeKyc(inquiryId: string, status: string, request: Request) {
    const redirect = new URL(request.url).searchParams.get('from')
    const user = window.Clerk?.user

    if (user && inquiryId) {
        const timestamp = new Date().toISOString().split('.')[0] + 'Z'
        const publicMetaData: UserPublicMetadata = {
            tera: parseTeraUserData(user?.publicMetadata?.tera),
            kyc: parseKycStatus({
                ...(user?.publicMetadata?.kyc || {}),
                id: inquiryId,
                status: status as KycStatus,
                updatedAt: timestamp,
                completedAt: timestamp,
            }),
        }
        user.publicMetadata = publicMetaData
    }

    if (redirect) {
        const allowedPatterns = [
            /^\/group\/[^/]+\/pledge\/review$/,
            /^\/group\/[^/]+\/add\/[^/]+$/,
        ]

        if (allowedPatterns.some((pattern) => pattern.test(redirect))) return replace(redirect)
    }

    return replace('../')
}

const KycVerifyRoute: RouteObject = {
    path: 'verify',
    element: <KycVerifyScreen />,
    loader,
    action,
}

export { KycVerifyRoute }
